import { PLACE_QUERY_DEBOUNCE_MS, placeQueryReady, type PlaceSuggestion } from "@vidario/domain";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useVidarioClient } from "../client";
import { queryKeys } from "../queryKeys";

// The same typed text gives the same places for a while; no need to ask again.
const PLACE_SUGGESTIONS_STALE_MS = 5 * 60 * 1000;

/** Places for the Standort while it's typed (#49): asked after a short pause
 * in typing, from two characters, and a request still running is cancelled
 * when the text changes. No retry: a down provider shows its message at once. */
export function usePlaceSuggestions(text: string): UseQueryResult<PlaceSuggestion[]> & {
  /** The text the shown places belong to (it trails the input while typing). */
  query: string;
} {
  const client = useVidarioClient();
  const query = useDebouncedValue(text.trim(), PLACE_QUERY_DEBOUNCE_MS);
  const result = useQuery({
    queryKey: queryKeys.placeSuggestions(query),
    queryFn: ({ signal }) => client.suggestPlaces(query, signal),
    enabled: placeQueryReady(query),
    staleTime: PLACE_SUGGESTIONS_STALE_MS,
    retry: false,
  });
  return { ...result, query };
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
